import EventEmitter from "eventemitter3";
import { DateLike, formatLogTimestamp } from "~/lib/date";
import JobLogStore from "~/stores/JobLogStore";

type HighlightedRange = {
  jobId: string;
  start: number;
  end: number;
};

type TimestampOptions = {
  timestampsOn: boolean;
  timestampsInUTC: boolean;
};

class JobLogLineStore {
  eventEmitter: EventEmitter;
  state: {
    [key: string]: boolean;
  };
  // Store for range-based highlighting (multi-line selection)
  highlightedRange: HighlightedRange | null;
  // Anchor line for shift+click range selection
  anchorLine: { jobId: string; lineNumber: number } | null;

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {};
    this.highlightedRange = null;
    this.anchorLine = null;
  }

  addChangeListener(lineId: string, callback: () => void) {
    return this.eventEmitter.addListener(`${lineId}/change`, callback);
  }

  removeChangeListener(lineId: string, callback: () => void) {
    return this.eventEmitter.removeListener(`${lineId}/change`, callback);
  }

  getLineNumberFromId(lineId: string): number | null {
    // Content line format: jobId/groupNumber-lineNumber (e.g., "abc123/5-42")
    const contentLineMatch = lineId.match(/-(\d+)$/);
    if (contentLineMatch) {
      return parseInt(contentLineMatch[1], 10);
    }

    // Group header format: jobId/groupNumber (e.g., "abc123/53")
    const groupHeaderMatch = lineId.match(/\/(\d+)$/);
    if (groupHeaderMatch) {
      return parseInt(groupHeaderMatch[1], 10);
    }

    return null;
  }

  getJobIdFromLineId(lineId: string): string {
    // Format: jobId/groupNumber-lineNumber (e.g., "abc123/5-42")
    const [jobId] = lineId.split("/");
    return jobId;
  }

  /**
   * Check if a line is highlighted.
   * Supports both legacy single-line highlighting and range highlighting.
   */
  isHighlighted(lineId: string): boolean {
    // First check legacy single-line state
    if (this.state[lineId]) {
      return true;
    }

    // Then check range highlighting
    if (this.highlightedRange) {
      const jobId = this.getJobIdFromLineId(lineId);
      if (jobId !== this.highlightedRange.jobId) {
        return false;
      }

      const lineNumber = this.getLineNumberFromId(lineId);
      if (lineNumber === null) {
        return false;
      }

      return lineNumber >= this.highlightedRange.start && lineNumber <= this.highlightedRange.end;
    }

    return false;
  }

  /**
   * Get the currently highlighted range for a job.
   * Returns null if no range is highlighted or if the job doesn't match.
   */
  getHighlightedRange(jobId: string): { start: number; end: number } | null {
    if (this.highlightedRange && this.highlightedRange.jobId === jobId) {
      const totalLines = JobLogStore.get(jobId)?.totalLines;
      return {
        start: this.highlightedRange.start,
        end: totalLines
          ? Math.min(this.highlightedRange.end, totalLines)
          : this.highlightedRange.end,
      };
    }
    return null;
  }

  /**
   * Check if a line number falls within the currently highlighted multi-line range.
   * Returns false for single-line selections (where start === end).
   */
  isLineInRange(jobId: string, lineNumber: number): boolean {
    const range = this.getHighlightedRange(jobId);
    if (!range || range.start === range.end) {
      return false;
    }
    return lineNumber >= range.start && lineNumber <= range.end;
  }

  /**
   * Get the anchor line for shift+click range selection.
   */
  getAnchorLine(): { jobId: string; lineNumber: number } | null {
    return this.anchorLine;
  }

  /**
   * Set the anchor line for shift+click range selection.
   */
  setAnchorLine(jobId: string, lineNumber: number) {
    this.anchorLine = { jobId, lineNumber };
  }

  /**
   * Highlight a range of lines within a job.
   * Clears any existing highlights for this job.
   */
  highlightRange(jobId: string, startLine: number, endLine: number) {
    // Normalize to ascending order
    const start = Math.min(startLine, endLine);
    const end = Math.max(startLine, endLine);

    this.highlightedRange = { jobId, start, end };

    // Clear any legacy single-line highlights for this job
    for (const key in this.state) {
      if (key.startsWith(jobId)) {
        delete this.state[key];
        this.eventEmitter.emit(`${key}/change`);
      }
    }

    this.eventEmitter.emit(`${jobId}/range-change`);
  }

  /**
   * Clear any highlighted range.
   */
  clearRange() {
    if (this.highlightedRange) {
      const { jobId } = this.highlightedRange;
      this.highlightedRange = null;
      this.eventEmitter.emit(`${jobId}/range-change`);
    }
  }

  /**
   * Clear all highlights (both single-line and range).
   */
  clearAll() {
    // Clear range highlight
    this.clearRange();

    // Clear single-line highlights
    for (const lineId in this.state) {
      delete this.state[lineId];
      this.eventEmitter.emit(`${lineId}/change`);
    }

    // Clear anchor line
    this.anchorLine = null;
  }

  /**
   * Legacy single-line highlight function.
   * When multi-line selection is disabled or for backward compatibility.
   */
  highlight(lineId: string) {
    // If the line is already highlighted, toggle it off.
    if (this.state[lineId]) {
      delete this.state[lineId];
      this.eventEmitter.emit(`${lineId}/change`);
      return;
    }

    // Line ID's are in a format of: `jobId/lineNumber`
    const [jobId] = lineId.split("/");

    // Clear any range highlight for this job
    if (this.highlightedRange && this.highlightedRange.jobId === jobId) {
      this.highlightedRange = null;
      this.eventEmitter.emit(`${jobId}/range-change`);
    }

    // Remove the highlight from the other line from this job
    for (const key in this.state) {
      if (key.indexOf(jobId) === 0) {
        delete this.state[key];
        this.eventEmitter.emit(`${key}/change`);
        break;
      }
    }

    // Highlight the new line and emit the change
    this.state[lineId] = true;

    // Set this as the anchor line for potential shift+click
    const lineNumber = this.getLineNumberFromId(lineId);
    if (lineNumber !== null) {
      this.setAnchorLine(jobId, lineNumber);
    }

    return this.eventEmitter.emit(`${lineId}/change`);
  }

  /**
   * Add listener for range change events.
   */
  addRangeChangeListener(jobId: string, callback: () => void) {
    return this.eventEmitter.addListener(`${jobId}/range-change`, callback);
  }

  /**
   * Remove listener for range change events.
   */
  removeRangeChangeListener(jobId: string, callback: () => void) {
    return this.eventEmitter.removeListener(`${jobId}/range-change`, callback);
  }

  /**
   * Get the text content of all highlighted lines for a job.
   * Returns lines joined by newlines, sorted by line number.
   *
   * This function supports both range highlighting (where a contiguous range of lines is highlighted)
   * and single-line highlighting (legacy mode, where only one line is highlighted).
   *
   * - If a range is highlighted for the job, returns the text of all lines in the range, joined by newlines.
   * - If a single line is highlighted (legacy), returns the text of that line.
   * - If there are no highlighted lines for the job, returns an empty string.
   *
   * When timestampOptions.timestampsOn is true, timestamps are prepended to each line.
   *
   * The returned lines are sorted by line number.
   */
  getHighlightedLinesText(jobId: string, timestampOptions?: TimestampOptions): string {
    const log = JobLogStore.get(jobId);
    if (!log || !log.groups) {
      return "";
    }

    const includeTimestamps = timestampOptions?.timestampsOn && log.linesHaveTimestamps;
    const utc = timestampOptions?.timestampsInUTC ?? false;

    // Collect all highlighted lines with their text content
    const highlightedLines: Array<{
      lineNumber: number;
      text: string;
      startedAt?: DateLike | null;
    }> = [];

    // Check for range highlighting
    const range = this.getHighlightedRange(jobId);

    // Check for single-line highlighting (legacy)
    const singleHighlightedLineId = Object.keys(this.state).find(
      (key) => this.state[key] && this.getJobIdFromLineId(key) === jobId,
    );
    const singleHighlightedLineNumber = singleHighlightedLineId
      ? this.getLineNumberFromId(singleHighlightedLineId)
      : null;

    if (!range && singleHighlightedLineNumber === null) {
      return "";
    }

    // Determine the end boundary for early termination
    const endBoundary = range ? range.end : singleHighlightedLineNumber;

    // Determine the start boundary for skipping groups
    const startBoundary = range ? range.start : singleHighlightedLineNumber;

    // Iterate through groups and lines to find highlighted content
    // TODO we could optimize performance in future by indexing lines by number rather
    // than using the grouped data structure provided by the JobLogStore.
    // Perhaps a flattened Map<lineNumber, <logLine>>, and refactoring the nested log data
    // structure to Map<groupRef, Array<lineNumber>> would give us a good balance between
    // memory and performance costs for large logs.
    for (const group of log.groups) {
      // Stop if we've passed the end boundary
      if (endBoundary !== null && group.number > endBoundary) {
        break;
      }

      // Skip groups that end before the start boundary
      if (startBoundary !== null && group.lines.length > 0) {
        const lastLineInGroup = group.lines[group.lines.length - 1];
        if (lastLineInGroup.number < startBoundary && group.number < startBoundary) {
          continue;
        }
      }

      // Check if the group header is highlighted
      if (range) {
        if (group.number >= range.start && group.number <= range.end) {
          highlightedLines.push({
            lineNumber: group.number,
            text: group.textContent,
            startedAt: group.startedAt,
          });
        }
      } else if (singleHighlightedLineNumber === group.number) {
        highlightedLines.push({
          lineNumber: group.number,
          text: group.textContent,
          startedAt: group.startedAt,
        });
      }

      // Check lines within the group
      for (const line of group.lines) {
        // Early termination: stop if we've passed the end boundary
        if (endBoundary !== null && line.number > endBoundary) {
          break;
        }

        if (range) {
          if (line.number >= range.start && line.number <= range.end) {
            highlightedLines.push({
              lineNumber: line.number,
              text: line.textContent,
              startedAt: line.startedAt,
            });
          }
        } else if (singleHighlightedLineNumber === line.number) {
          highlightedLines.push({
            lineNumber: line.number,
            text: line.textContent,
            startedAt: line.startedAt,
          });
        }
      }
    }

    // Sort by line number and join with newlines
    return highlightedLines
      .sort((lineA, lineB) => lineA.lineNumber - lineB.lineNumber)
      .map((line) => {
        if (includeTimestamps && line.startedAt) {
          const timestamp = formatLogTimestamp(line.startedAt, utc);
          return `${timestamp}\t${line.text}`;
        }
        return line.text;
      })
      .join("\n");
  }

  /**
   * Get the text content of one or more lines for a job, independent of the
   * highlighted range in the store. For a single line pass start === end.
   */
  getTextForLines(
    jobId: string,
    startLine: number,
    endLine: number,
    timestampOptions?: TimestampOptions,
  ): string {
    const log = JobLogStore.get(jobId);
    if (!log || !log.groups) {
      return "";
    }

    const includeTimestamps = timestampOptions?.timestampsOn && log.linesHaveTimestamps;
    const utc = timestampOptions?.timestampsInUTC ?? false;

    const lines: Array<{ lineNumber: number; text: string; startedAt?: DateLike | null }> = [];

    for (const group of log.groups) {
      if (group.number > endLine) {
        break;
      }

      if (group.number >= startLine && group.number <= endLine) {
        lines.push({
          lineNumber: group.number,
          text: group.textContent,
          startedAt: group.startedAt,
        });
      }

      for (const line of group.lines) {
        if (line.number > endLine) {
          break;
        }
        if (line.number >= startLine) {
          lines.push({
            lineNumber: line.number,
            text: line.textContent,
            startedAt: line.startedAt,
          });
        }
      }
    }

    return lines
      .map((line) => {
        if (includeTimestamps && line.startedAt) {
          return `${formatLogTimestamp(line.startedAt, utc)}\t${line.text}`;
        }
        return line.text;
      })
      .join("\n");
  }

  /**
   * Determines if the copy button should be visible for a given line.
   *
   * - For multi-line ranges: only the first line of the range shows the button
   * - For single line selections: the highlighted line shows the button
   *
   * This is used both for rendering the button and for determining if the row
   * needs elevated z-index to prevent clipping.
   */
  shouldShowCopyButton(jobId: string, lineNumber: number): boolean {
    const range = this.getHighlightedRange(jobId);

    // For multi-line ranges, only the first line shows the button
    if (range && range.start !== range.end) {
      return lineNumber === range.start;
    }

    // For single-line range (start === end), show button on that line
    if (range && range.start === range.end) {
      return lineNumber === range.start;
    }

    // Check for legacy single-line highlight - find the highlighted line for this job
    for (const key in this.state) {
      if (this.state[key] && this.getJobIdFromLineId(key) === jobId) {
        const highlightedLineNumber = this.getLineNumberFromId(key);
        return highlightedLineNumber === lineNumber;
      }
    }

    return false;
  }
}

export default new JobLogLineStore();
